import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const provider = params.get("provider");
  const [status, setStatus] = useState(provider === "razorpay" ? "paid" : "polling");
  const [orderId, setOrderId] = useState(params.get("order_id"));
  const attempts = useRef(0);
  const { clear } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (provider === "razorpay") return; // already verified server-side
    if (!sessionId) { setStatus("error"); return; }
    let stopped = false;
    async function tick() {
      if (stopped) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrderId(data.order_id);
          setStatus("paid");
          clear();
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
      } catch { /* keep polling */ }
      if (attempts.current > 15) { setStatus("timeout"); return; }
      setTimeout(tick, 2000);
    }
    tick();
    return () => { stopped = true; };
  }, [sessionId, provider, clear]);

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      {status === "polling" && (
        <>
          <Loader2 className="w-14 h-14 text-[#4A148C] mx-auto animate-spin" />
          <h1 className="font-display text-4xl text-[#2C1810] mt-6">Confirming your payment…</h1>
          <p className="font-body text-[#5B3D2A] mt-3">Please wait while we verify your transaction with Stripe.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-16 h-16 text-[#E65C00] mx-auto" />
          <h1 data-testid="payment-success-heading" className="font-display text-5xl text-[#2C1810] mt-6">Order confirmed!</h1>
          <p className="font-body text-[#5B3D2A] mt-4">
            Thank you for shopping with Mona&apos;s Creations. Your festive treasures are being prepared with love.
          </p>
          <div className="flex gap-3 justify-center mt-8">
            <Button data-testid="success-view-orders" onClick={() => navigate(`/orders`)} className="rounded-full bg-[#E65C00] hover:bg-[#C44A00] text-white px-6 btn-lift">
              View my orders <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button variant="outline" onClick={() => navigate("/shop")} className="rounded-full border-[#4A148C] text-[#4A148C] px-6">
              Continue shopping
            </Button>
          </div>
        </>
      )}
      {(status === "failed" || status === "timeout" || status === "error") && (
        <>
          <XCircle className="w-14 h-14 text-[#4A148C] mx-auto" />
          <h1 className="font-display text-4xl text-[#2C1810] mt-6">Payment not confirmed</h1>
          <p className="font-body text-[#5B3D2A] mt-3">
            We couldn't verify your payment yet. Please check your orders page later, or try again.
          </p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/orders" className="text-[#4A148C] underline">My orders</Link>
            <Link to="/cart" className="text-[#E65C00] underline">Back to cart</Link>
          </div>
        </>
      )}
    </div>
  );
}
